import { useState, useRef, useEffect } from "react";
import "./Speedometer2.css";


export default function Speedometer2({OverallData}) {
  const [value, setValue] = useState(0.18);
  const svgRef     = useRef(null);
  const isDragging = useRef(false);


  useEffect(() => {
    if (OverallData !== undefined && OverallData !== null) {
      const normalized = Math.max(0, Math.min(1, OverallData / 100));
      setValue(normalized);
    }
  }, [OverallData]);

  // ── Layout logic ──
  // Dome div: 420 × 210px  →  semicircle, flat bottom at y=210 (screen)
  // SVG viewBox: 420 × 260  (extra 50px below CY so hub is fully visible)
  // CX=210, CY=210  →  hub center sits on the flat bottom line of the dome
  // SVG is position:absolute bottom:0, so viewBox y=210 = dome bottom = screen flat line
  // The extra 50px of viewBox (y=210..260) hangs below the dome → hub fully visible
  const CX = 210;
  const CY = 210;

  const segments = [
    "#72d9c4", "#82d8b4", "#98d8a0", "#b4d890",
    "#ccd882", "#d8cc74", "#d8b066", "#d89060",
    "#d07878", "#c068b0", "#a860cc", "#8860dc",
  ];

  const TOTAL   = segments.length;
  const GAP_DEG = 1;
  const SEG_DEG = 180 / TOTAL;

  // Radii
  const R_SEG_OUT = 168;   // segment outer edge  (dome radius ≈ 210, gap = 210-168 = 42px)
  const R_SEG_IN  = 118;   // segment inner edge
  // Dome is 30px smaller radius than R_SEG_IN → clear 30px white gap
  const R_DOME    = 88;    // R_SEG_IN(118) - 30 = 88

  function pt(r, deg) {
    const rad = deg * Math.PI / 180;
    return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
  }

  function ringPath(rOut, rIn, startDeg, endDeg) {
    const s1 = pt(rOut, startDeg);
    const e1 = pt(rOut, endDeg);
    const s2 = pt(rIn,  endDeg);
    const e2 = pt(rIn,  startDeg);
    const lg = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    return (
      `M ${s1.x} ${s1.y}` +
      ` A ${rOut} ${rOut} 0 ${lg} 1 ${e1.x} ${e1.y}` +
      ` L ${s2.x} ${s2.y}` +
      ` A ${rIn} ${rIn} 0 ${lg} 0 ${e2.x} ${e2.y}` +
      ` Z`
    );
  }

  // Solid lavender semicircle — flat bottom at CY
  function domePath(r) {
    const left  = { x: CX - r, y: CY };
    const right = { x: CX + r, y: CY };
    return `M ${left.x} ${left.y} A ${r} ${r} 0 0 1 ${right.x} ${right.y} Z`;
  }

  // ── Needle ──
  const needleDeg = -180 + value * 180;
  const TIP_R     = R_SEG_OUT - 10;
  const BASE_HALF = 12;

  const tip  = pt(TIP_R, needleDeg);
  const pA   = needleDeg + 90;
  const pB   = needleDeg - 90;
  const bL   = { x: CX + BASE_HALF * Math.cos(pA * Math.PI / 180), y: CY + BASE_HALF * Math.sin(pA * Math.PI / 180) };
  const bR   = { x: CX + BASE_HALF * Math.cos(pB * Math.PI / 180), y: CY + BASE_HALF * Math.sin(pB * Math.PI / 180) };
  const needlePoints = `${tip.x},${tip.y} ${bL.x},${bL.y} ${bR.x},${bR.y}`;

  const getVal = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const sx = 420 / rect.width;
    const sy = 260 / rect.height;
    const x  = (clientX - rect.left) * sx - CX;
    const y  = (clientY - rect.top)  * sy - CY;
    if (y > 50) return;
    let deg = Math.atan2(y, x) * 180 / Math.PI;
    deg = Math.max(-180, Math.min(0, deg));
    setValue((deg + 180) / 180);
  };

  useEffect(() => {
    const up = () => { isDragging.current = false; };
    window.addEventListener("mouseup",  up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mouseup",  up);
      window.removeEventListener("touchend", up);
    };
  }, []);

  return (
    <div className="page">
      <div className="card">

        {/* Outer dome — HTML half-ellipse with inset arc shadow */}
        <div className="outer-dome">
          <svg
            ref={svgRef}
            className="gauge-svg"
            viewBox="0 0 420 260"
            width="420"
            onMouseDown={e  => { isDragging.current = true;  getVal(e.clientX, e.clientY); }}
            onMouseMove={e  => { if (isDragging.current) getVal(e.clientX, e.clientY); }}
            onTouchStart={e => { isDragging.current = true;  getVal(e.touches[0].clientX, e.touches[0].clientY); }}
            onTouchMove={e  => { if (isDragging.current) getVal(e.touches[0].clientX, e.touches[0].clientY); }}
          >
            <defs>
              <radialGradient id="domeGrad" cx="50%" cy="100%" r="70%">
                <stop offset="0%"   stopColor="#efd8fa" stopOpacity="1" />
                 <stop offset="70%"   stopColor="#efd8fa" stopOpacity="1" />
                <stop offset="100%" stopColor="#e0baf1" stopOpacity="0.9" />
              </radialGradient>

              <radialGradient id="hubRing" cx="44%" cy="38%" r="62%">
                <stop offset="0%"   stopColor="#ffffff" />
                <stop offset="100%" stopColor="#ccc0f0" />
              </radialGradient>

              <radialGradient id="hubBlue" cx="38%" cy="33%" r="65%">
                <stop offset="0%"   stopColor="#87C6E8 " />
                <stop offset="0%"  stopColor="#345364b4 " />
                <stop offset="80%" stopColor="#345364" />
              </radialGradient>

              <filter id="needleShadow" x="-80%" y="-80%" width="260%" height="260%">
                <feDropShadow dx="0" dy="1.5" stdDeviation="2.5" floodColor="#69A8CA" floodOpacity="0.9" />
              </filter>

              <filter id="hubShadow" x="-80%" y="-80%" width="260%" height="260%">
                <feDropShadow dx="0" dy="2" stdDeviation="8" floodColor="#6040b8" floodOpacity="0.3" />
              </filter>
            </defs>

            {/* 1 ── Colored segments — always fully visible */}
            {segments.map((color, i) => {
              const a1 = -180 + i * SEG_DEG + GAP_DEG / 2;
              const a2 = -180 + (i + 1) * SEG_DEG - GAP_DEG / 2;
              return (
                <path
                  key={i}
                  className="seg"
                  d={ringPath(R_SEG_OUT, R_SEG_IN, a1, a2)}
                  fill={color}
                />
              );
            })}

            {/* 2 ── Lavender dome — 30px gap below segments inner edge */}
            <path d={domePath(R_DOME)} fill="url(#domeGrad)" />

            {/* 3 ── Needle */}
            <polygon
              className={isDragging.current ? "needle-dragging" : "needle-shape"}
              points={needlePoints}
              fill="#7060c8"
              filter="url(#needleShadow)"
            />

            {/* 4 ── Hub — fully visible because viewBox extends 50px below CY */}
            <circle className="hub-glow-circle" cx={CX} cy={CY} r="28" filter="url(#hubShadow)" />
            <circle cx={CX} cy={CY} r="20" fill="url(#hubRing)" className="hub-outer-ring" />
            <circle cx={CX} cy={CY} r="13" fill="url(#hubBlue)" />
            <circle cx={CX} cy={CY} r="5"  className="hub-center-dot" />
          </svg>
        </div>

        {/* Labels — margin-top clears the hub that hangs below dome */}
        <div className="labels-row">
          <span className="label-text">Deliberate</span>
          <span className="label-text">Accelerated</span>
        </div>
      </div>
    </div>
  );
}